import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DoorOpen, DoorClosed, Users, TrendingDown, TrendingUp, RefreshCw, AlertTriangle, Store, Lock, Mail } from "lucide-react";
import { curSym } from "@/lib/company";

const tl = (n) => (Number(n) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " " + curSym();
const dur = (m) => { if (m == null) return ""; if (m < 60) return `${m} dk`; const h = Math.floor(m / 60); return `${h} sa ${m % 60} dk`; };

const TONES = { slate: "text-slate-700", green: "text-emerald-600", red: "text-red-600" };

function KPI({ icon: Icon, label, value, tone = "slate", testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`font-heading text-xl font-bold tabular ${TONES[tone] || TONES.slate}`}>{value}</div>
    </div>
  );
}

function Spark({ data }) {
  const pts = data || [];
  const max = Math.max(1, ...pts.map((p) => Math.abs(p.net || 0)));
  return (
    <div className="mt-1 flex items-end gap-0.5" data-testid="cp-spark">
      {pts.map((p, i) => {
        const neg = (p.net || 0) < -0.005;
        const pos = (p.net || 0) > 0.005;
        const h = p.count ? Math.max(3, Math.round((Math.abs(p.net || 0) / max) * 22)) : 2;
        return (
          <div key={i} className="flex flex-1 flex-col items-center justify-end" style={{ height: 24 }}
            title={`${p.d}: ${p.count ? (p.net >= 0 ? "+" : "") + Number(p.net).toFixed(2) + " " + curSym() : "kayıt yok"}`}>
            <div className={`w-full rounded-sm ${!p.count ? "bg-slate-200" : neg ? "bg-red-400" : pos ? "bg-emerald-400" : "bg-slate-300"}`} style={{ height: h }} />
          </div>
        );
      })}
    </div>
  );
}

export default function RegisterControlPanel() {
  const { can } = useAuth();
  const canClose = can("shift.close.approve");
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closeTarget, setCloseTarget] = useState(null);
  const [counted, setCounted] = useState("0");
  const [adminPin, setAdminPin] = useState("");
  const [closing, setClosing] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/registers/control-panel?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Kontrol paneli yüklenemedi"))
      .finally(() => setLoading(false));
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const submitAdminClose = async () => {
    if (!closeTarget) return;
    if (counted === "" || isNaN(parseFloat(counted))) { toast.error("Sayılan nakit girin"); return; }
    setClosing(true);
    try {
      const { data: rep } = await api.post(`/shifts/${closeTarget.shift_id}/admin-close`, { counted_cash: parseFloat(counted), pin: adminPin || null });
      const ov = rep?.over_short ?? rep?.report?.over_short;
      toast.success(ov == null
        ? `Kasa kapatıldı — Z raporu alındı (${closeTarget.cashier_name})`
        : `Kasa kapatıldı (${closeTarget.cashier_name}) — ${ov < 0 ? "Açık" : ov > 0 ? "Fazla" : "Denk"} ${tl(Math.abs(ov))}`);
      setCloseTarget(null); setCounted("0"); setAdminPin("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kasa kapatılamadı"); } finally { setClosing(false); }
  };

  const notifyWatchlist = async () => {
    setNotifying(true);
    try {
      const { data: r } = await api.post(`/registers/control-panel/notify-watchlist?days=${days}`);
      if (!r.count) toast.info("Bildirilecek kasiyer yok");
      else if (r.sent > 0) toast.success(`${r.count} kasiyer için ${r.sent} amire e-posta gönderildi`);
      else toast.warning(`${r.count} kasiyer listelendi; e-posta gönderilemedi (yapılandırma gerekli), uygulama içi bildirim iletildi`);
    } catch (e) { toast.error(e.response?.data?.detail || "Bildirim gönderilemedi"); } finally { setNotifying(false); }
  };

  const k = data?.kpis || {};
  const net = Number(k.period_net_over_short || 0);

  return (
    <div className="space-y-5 p-4 sm:p-6" data-testid="register-control-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Kasa Yönetimi</p>
          <h2 className="font-heading text-2xl font-bold text-slate-800">Kasa Kontrol Paneli</h2>
          <p className="text-sm text-slate-500">Tüm lokasyonların kasaları, vardiyadaki personel ve kasa fazla/eksik trendi.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="cp-days">
            {[7, 14, 30].map((d) => (
              <button key={d} data-testid={`cp-days-${d}`} onClick={() => setDays(d)}
                className={`rounded-md px-3 py-1.5 text-xs font-bold transition-colors ${days === d ? "bg-white text-brand-green shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-700"}`}>
                {d} gün
              </button>
            ))}
          </div>
          <button data-testid="cp-refresh" onClick={load}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Yenile
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="cp-kpis">
        <KPI icon={Store} label="Toplam Kasa" value={k.registers_total ?? 0} testid="cp-kpi-registers" />
        <KPI icon={DoorOpen} label="Şu An Açık" value={k.open_now ?? 0} tone="green" testid="cp-kpi-open" />
        <KPI icon={Users} label="Vardiyada" value={k.cashiers_on_duty ?? 0} testid="cp-kpi-onduty" />
        <KPI icon={net < 0 ? TrendingDown : TrendingUp} label={`Net (${data?.days ?? days}g)`} value={tl(net)}
          tone={net < 0 ? "red" : net > 0 ? "green" : "slate"} testid="cp-kpi-net" />
        <KPI icon={AlertTriangle} label="Eksik Vardiya" value={k.period_short_count ?? 0}
          tone={(k.period_short_count ?? 0) > 0 ? "red" : "slate"} testid="cp-kpi-short" />
      </div>

      {loading && !data ? (
        <div className="py-16 text-center text-sm text-slate-400" data-testid="cp-loading">Yükleniyor…</div>
      ) : (data?.locations || []).length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-16 text-center text-sm text-slate-400" data-testid="cp-empty">
          Kasa bulunamadı. Kasalar sekmesinden kasa tanımlayın.
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="cp-locations">
          {(data.locations || []).map((loc, i) => {
            const t = loc.trend || {};
            const lnet = Number(t.net_over_short || 0);
            return (
              <div key={loc.location_id || i} data-testid={`cp-loc-${i}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <Store className="h-4 w-4 shrink-0 text-brand-green" />
                    <span className="truncate font-heading font-bold text-slate-800">{loc.location_name}</span>
                    <span className="shrink-0 text-xs text-slate-400">· {(loc.registers || []).length} kasa</span>
                  </div>
                  <span data-testid={`cp-loc-net-${i}`}
                    className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${lnet < 0 ? "bg-red-50 text-red-600" : lnet > 0 ? "bg-emerald-50 text-emerald-600" : "bg-slate-50 text-slate-500"}`}>
                    {t.shifts ? `${lnet < 0 ? "Eksik" : lnet > 0 ? "Fazla" : "Denk"} ${tl(lnet)}` : "Kayıt yok"}
                  </span>
                </div>
                <div className="space-y-2">
                  {(loc.registers || []).map((r, j) => (
                    <div key={r.id || j} data-testid={`cp-reg-${i}-${j}`} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-semibold text-slate-700">{r.name}</span>
                          {r.code && <span className="shrink-0 text-[10px] text-slate-400">{r.code}</span>}
                          {r.has_password && <Lock className="h-3 w-3 shrink-0 text-slate-300" />}
                          {r.active === false && <span className="shrink-0 text-[10px] font-semibold text-slate-400">(pasif)</span>}
                        </div>
                        {r.assigned_name && <div className="truncate text-[11px] text-slate-400">Atanan: {r.assigned_name}</div>}
                      </div>
                      <div className="shrink-0 text-right">
                        {r.open && r.open_shift ? (
                          <div>
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-600">
                              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> Açık
                            </span>
                            <div className="text-[11px] text-slate-500">{r.open_shift.cashier_name} · {dur(r.open_shift.since_minutes)}</div>
                            {r.open_shift.since_minutes > 720 && (
                              <div data-testid={`cp-longshift-${i}-${j}`} className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-600">
                                <AlertTriangle className="h-2.5 w-2.5" /> Uzun vardiya
                              </div>
                            )}
                            {canClose && r.open_shift.shift_id && (
                              <button data-testid={`cp-close-${i}-${j}`}
                                onClick={() => { setCloseTarget({ ...r.open_shift, register_name: r.name, location_name: loc.location_name }); setCounted("0"); setAdminPin(""); }}
                                className="mt-1 inline-flex items-center gap-1 rounded-md bg-brand-green px-2 py-1 text-[11px] font-bold text-white transition-colors hover:bg-brand-green-600">
                                <DoorClosed className="h-3 w-3" /> Z Al / Kapat
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-400">Kapalı</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {(loc.registers || []).length === 0 && <div className="py-3 text-center text-xs text-slate-400">Bu lokasyonda kasa yok</div>}
                </div>
                {t.shifts > 0 && (
                  <div className="mt-3 flex flex-wrap gap-3 border-t border-slate-100 pt-2 text-[11px] text-slate-500">
                    <span>Vardiya: <b className="text-slate-700">{t.shifts}</b></span>
                    <span className="text-red-500">Eksik: <b>{t.short_count}</b></span>
                    <span className="text-emerald-600">Fazla: <b>{t.over_count}</b></span>
                    <span>Denk: <b className="text-slate-700">{t.balanced_count}</b></span>
                  </div>
                )}
                {(t.spark || []).some((p) => p.count) && (
                  <div className="mt-2" data-testid={`cp-spark-wrap-${i}`}>
                    <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Günlük Kasa Farkı · son {(t.spark || []).length} gün</div>
                    <Spark data={t.spark} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(data?.watchlist || []).length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid="cp-watchlist">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <span className="font-heading font-bold text-slate-800">Dikkat Listesi — Sürekli Kasa Eksiği</span>
            </div>
            <button data-testid="cp-watch-notify" onClick={notifyWatchlist} disabled={notifying}
              className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-amber-600 disabled:opacity-50">
              <Mail className="h-3.5 w-3.5" /> {notifying ? "Gönderiliyor…" : "Amirlere Bildir"}
            </button>
          </div>
          <div className="space-y-1.5">
            {(data.watchlist || []).map((w, i) => (
              <div key={w.cashier || i} data-testid={`cp-watch-${i}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-sm">
                <span className="font-semibold text-slate-700">{w.cashier_name}</span>
                <span className="text-xs text-slate-500">{w.short_count} kez eksik · Toplam <b className="text-red-600">{tl(w.total_short)}</b> · Net {tl(w.net)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!closeTarget} onOpenChange={(o) => !o && setCloseTarget(null)}>
        <DialogContent data-testid="cp-close-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Kasayı Kapat — Z Raporu Al</DialogTitle>
            <DialogDescription>{closeTarget?.register_name} · {closeTarget?.cashier_name} · {closeTarget?.location_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Sayılan Nakit ({curSym()})</label>
              <input data-testid="cp-close-counted" inputMode="decimal" value={counted} onChange={(e) => setCounted(e.target.value)}
                className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm tabular focus:outline-none focus:ring-2 focus:ring-brand-green" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">PIN Kodunuz</label>
              <input data-testid="cp-close-pin" type="password" inputMode="numeric" value={adminPin} onChange={(e) => setAdminPin(e.target.value.replace(/\D/g, ""))}
                placeholder="Yönetici PIN'iniz" className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-brand-green" />
              <p className="mt-1 text-[11px] text-slate-400">Kimliğinizi doğrulamak için kendi PIN'inizi girin. Big BOSS PIN'siz de kapatabilir.</p>
            </div>
            <Button data-testid="cp-close-submit" onClick={submitAdminClose} disabled={closing} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
              {closing ? "Kapatılıyor…" : "Z Raporunu Kaydet & Kasayı Kapat"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
